import { useEffect, useMemo, useState } from 'react'
import { Responsive, WidthProvider } from 'react-grid-layout'

import { useDroppable } from '@dnd-kit/core'

import { useSelector } from '@store/index'
import { selectCurrentPage, selectHeaderCollapsed } from '@store/slices/navigation'

import { findWidgetById, getResponsiveHeight } from '@pages/admin/layouts/utils'

import { useLayoutContext } from 'contexts/LayoutContext'

import { extractLayoutAttributes } from '../../../helpers'
import {
  Breakpoint,
  BreakpointCols,
  LayoutDisplayMode,
  LayoutItem,
  ViewMode,
  isViewModeEdit,
  isViewModeParentView
} from '../../../types'
import LayoutComponent from '../layout-component'

import './freestyle-section.css'

const ResponsiveReactGridLayout = WidthProvider(Responsive)

interface FreeStyleSectionProps {
  id: number
  gridLine: boolean
  devMode: boolean
  path: string
  section: any
  onLayoutChange?: (layout: any, layouts: any, props: any) => void
  onComponentRemove?: (path: string, layout_item: any) => void
  className?: string
  rowHeight?: number
  mode: ViewMode
  cols?: { [key: string]: number }
  relative_layout_structure?: any
  isDraggable?: boolean
  isResizable?: boolean
  layoutSettings?: any
}

export function FreeStyleSection(props: FreeStyleSectionProps) {
  const {
    layoutSettings,
    mode,
    gridLine,
    devMode,
    path,
    section,
    cols,
    relative_layout_structure = []
  } = props
  const { children: columns } = section
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight)

  const currentPage = useSelector(selectCurrentPage)
  const headerCollapsed = useSelector(selectHeaderCollapsed)
  const hasSearchFilters = useMemo(() => {
    const filters = currentPage?.pageSettings?.settings?.search_filters
    return filters && Object.keys(filters).length > 0
  }, [currentPage?.pageSettings])

  const { isOver, setNodeRef } = useDroppable({
    id: props.id,
    data: {
      ...section,
      path: path,
      cols: cols
    }
  })

  const layoutData = extractLayoutAttributes(section)

  // TODO: Figure out diff between lg, md, sm
  const compactType = 'vertical'
  const [currentBreakpoint, setCurrentBreakpoint] = useState(Breakpoint.LG)
  const [mounted, setMounted] = useState(false)
  const [layouts, setLayouts] = useState<{ [key: string]: LayoutItem[] }>({
    lg: layoutData || []
  })

  const { setSelectedWidget, selectedWidgets, setSelectedWidgets } = useLayoutContext()

  const onBreakpointChange = (breakpoint: string) => {
    setCurrentBreakpoint(breakpoint as Breakpoint)
  }

  const onLayoutChange = (layout: any, layouts: any) => {
    if (props.onLayoutChange) {
      props.onLayoutChange(layout, layouts, props)
    }
  }

  const onRemoveItem = (layout_item: LayoutItem) => {
    const newLayouts = layouts.lg.filter((widget) => widget.i !== layout_item.i)
    setLayouts({
      ...layouts,
      lg: newLayouts
    })

    if (props.onComponentRemove) {
      props.onComponentRemove(path, layout_item)
    }
  }

  // Single child full height/width handling
  useEffect(() => {
    if (layoutData.length === 1 && layoutSettings?.display_mode === LayoutDisplayMode.FullWidth) {
      let componentHeight = getResponsiveHeight()

      if (hasSearchFilters && !headerCollapsed) {
        componentHeight -= 0.5
      }

      const updatedLayout = layoutData.map((item: any) => ({
        ...item,
        w: BreakpointCols.lg,
        h: componentHeight
      }))

      const isSameLayout =
        layouts.lg?.length === updatedLayout.length &&
        layouts.lg.every(
          (layoutItem: any, idx: number) =>
            layoutItem.w === updatedLayout[idx].w && layoutItem.h === updatedLayout[idx].h
        )

      if (!isSameLayout) {
        setLayouts((prevLayouts: any) => ({
          ...prevLayouts,
          lg: updatedLayout
        }))
      }
    }
  }, [
    layoutData,
    layoutSettings,
    layouts,
    currentBreakpoint,
    viewportHeight,
    hasSearchFilters,
    headerCollapsed
  ])

  useEffect(() => {
    if (layoutData.length > 0 && layouts.lg.length !== layoutData.length) {
      setLayouts((prevLayouts: any) => ({
        ...prevLayouts,
        lg: layoutData || []
      }))
    }
  }, [layoutData, layoutData.length, layouts.lg.length])

  useEffect(() => {
    setMounted(true)
  }, [])

  const onWidgetClick = (widget: any, componentViewMode: ViewMode) => {
    if (isViewModeEdit(componentViewMode)) {
      setSelectedWidget(widget)
    } else if (isViewModeParentView(componentViewMode)) {
      const isComponentSelected = selectedWidgets.some((selected) => selected.id === widget.id)

      if (isComponentSelected) {
        setSelectedWidgets((prevSelected) =>
          prevSelected.filter((selected) => selected.id !== widget.id)
        )
      } else {
        setSelectedWidgets((prevSelected) => [...prevSelected, widget])
      }
    }
  }

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <>
      <div ref={setNodeRef}>
        <div>
          {devMode && (
            <>
              <div>
                Current Breakpoint: {currentBreakpoint} (
                {props.cols?.[currentBreakpoint] || BreakpointCols.lg}
                columns)
              </div>
              <div className='layout-log'>
                Displayed as <code>[x, y, w, h]</code>:
                <div className='layout-items-container'>
                  {layouts.lg.map((l: any) => (
                    <div className='layout-item-container' key={l.i}>
                      <label className='layout-item-key'>{l.i}</label>:{' '}
                      <span className='layout-item-value'>
                        [{l.x}, {l.y}, {l.w}, {l.h}]
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        <div>
          <ResponsiveReactGridLayout
            className={` ${gridLine ? 'layout-grid' : ''} layout-mode-${mode} ${mode !== ViewMode.edit ? 'layout-view' : ''}`}
            style={{
              backgroundColor: isOver ? 'rgb(9, 113, 241)' : ''
            }}
            {...props}
            layouts={layouts}
            {...(isViewModeEdit(mode)
              ? {
                  breakpoint: Breakpoint.LG,
                  breakpoints: { lg: 1200 }
                }
              : {})}
            onBreakpointChange={onBreakpointChange}
            onLayoutChange={onLayoutChange}
            measureBeforeMount={false}
            useCSSTransforms={mounted}
            compactType={compactType}
            preventCollision={!compactType}
            draggableHandle='.layout-title-bar' // Remove this to make it full body draggable
            containerPadding={[0, 0]}
            margin={[16, 16]}
          >
            {_.map(layouts.lg, (layout_item, item_index) => {
              const widget = findWidgetById(layout_item.i, columns)

              let componentMatch = false
              if (isViewModeParentView(mode)) {
                const similarWidget = findWidgetById(
                  layout_item.i,
                  relative_layout_structure[0].children
                )
                if (similarWidget) {
                  componentMatch = true
                }
              }

              const isWidgetInArray = selectedWidgets.some((item) => item.id === widget.id)
              return (
                <div key={layout_item.i} className={layout_item.static ? 'static' : ''}>
                  <LayoutComponent
                    layout_item={layout_item}
                    widget={widget}
                    item_index={item_index}
                    rowHeight={props.rowHeight}
                    mode={mode}
                    onRemoveItem={onRemoveItem}
                    parentHasComponent={componentMatch}
                    onWidgetClick={onWidgetClick}
                    selected={isWidgetInArray}
                  />
                </div>
              )
            })}
          </ResponsiveReactGridLayout>
        </div>
      </div>
    </>
  )
}

FreeStyleSection.defaultProps = {
  rowHeight: 60,
  cols: { lg: 12, md: 9, sm: 6, xs: 3, xxs: 3 }
}

export default FreeStyleSection
